import type { InputHTMLAttributes, ReactNode, Ref } from 'react';
import { forwardRef } from 'react';
import { useIntl } from 'react-intl';

import { generateDataTestId } from '@/lib/tests';
import { formMessages } from '@/messages/forms';

import * as S from './Input.styles';

export type InputColorVariant =
  | 'default'
  | 'defaultInverted'
  | 'white'
  | 'grey'
  | 'transparent'
  | 'primaryLight'
  | 'secondaryLightGrey';

export type InputBorderColorVariant = 'default' | 'lightGray';

export type ErrorVariant = 'default' | 'full';

export interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  borderColorVariant?: InputBorderColorVariant;
  colorVariant?: InputColorVariant;
  errorVariant?: ErrorVariant;
  hasError?: boolean;
  label?: ReactNode;
  prefix?: string;
}

const generateAriaLabel = (ariaLabel: string) => {
  if (!ariaLabel) {
    return {};
  }

  return {
    'aria-label': ariaLabel,
  };
};

export const Input = forwardRef(
  (
    {
      colorVariant = 'white',
      disabled = false,
      errorVariant = 'default',
      hasError = false,
      label,
      prefix,
      ...props
    }: InputProps,
    ref: Ref<HTMLInputElement>,
  ) => {
    const { formatMessage } = useIntl();

    const optionalizedLabel = (label: ReactNode) => {
      if (typeof label === 'string' && label?.includes('Optional')) {
        return label.replace('Optional', formatMessage(formMessages.optional));
      }
      return label;
    };
    return (
      <S.Wrapper colorVariant={colorVariant} data-test-id='InputWrapper'>
        <S.InputContainer $hasPrefix={!!prefix}>
          {prefix && (
            <S.Prefix $error={hasError} $hasLabel={!!label} className='prefix'>
              {prefix}
            </S.Prefix>
          )}
          {label ? (
            <S.Input
              data-test-id='InputField'
              {...generateDataTestId('input', props.name || props.id || '')}
              ref={ref}
              $errorVariant={errorVariant}
              disabled={disabled}
              error={hasError}
              {...props}
            />
          ) : (
            <S.InputNoLabel
              data-test-id='InputField'
              {...generateDataTestId('input', props.name || props.id || '')}
              {...generateAriaLabel(props.placeholder || '')}
              ref={ref}
              $errorVariant={errorVariant}
              disabled={disabled}
              error={hasError}
              {...props}
            />
          )}

          {label ? (
            <S.Label {...{ htmlFor: props.id }} data-test-id='InputLabel'>
              {optionalizedLabel(label)}
            </S.Label>
          ) : null}
        </S.InputContainer>
      </S.Wrapper>
    );
  },
);

Input.displayName = 'Input';
